<template>
  <div
    class="min-h-screen bg-primary w-full h-full mt-[60px] lg:mt-0 themeTransition"
    ref="bodyRef"
  >
    <div v-if="!isLoading" class="sectionContainer place-content-start">
      <div class="slider fixed h-screen w-screen bg-orange z-40" />

      <page-wrapper v-if="pageGUI" :page-gui="pageGUI">
        <!-- content begin -->
        <div
          class="pt-[100px] col-start-2 col-span-12 flex flex-col lg:flex-row md:col-start-2 md:col-span-10 lg:col-start-2 lg:col-span-11 lg:mb-[120px]"
        >
          <h1 v-if="properties?.title" :class="headerStyle">
            {{ properties?.title }}
          </h1>

          <rich-text
            v-if="properties?.description.markup"
            :text="properties?.description.markup"
            :isUmbraco="isUmbraco"
            :theme="themeType"
            classes="mb-20 flex-1 lg:mb-0"
          />
        </div>

        <div
          class="relative flex overflow-x-auto 2xl:overflow-x-visible no-scrollbar col-start-1 lg:col-start-3 col-span-full mb-10 lg:mb-[60px]"
        >
          <filter-tag
            v-for="tag in tags"
            :label="tag.title"
            :selected="tag.id === activeFilter"
            :sectorToggle="isTagHidden(tag)"
            :theme="themeType"
            classes="shrink-0 mr-3 first:mr-3 md:last:mr-8 lg:last:mr-[108px] first:ml-5 md:first:ml-8 lg:first:ml-0"
            @handleClick="activeFilter = tag.id"
          />
        </div>
        <projects-grid :projects="projectCards" :theme="theme" :is-umbraco="isUmbraco" />

        <div
          v-if="showLoadMore"
          class="col-start-6 col-span-4 md:col-span-2 md:col-start-7 mb-36 mt-10"
        >
          <custom-button
            label="Load more"
            theme="light"
            type="secondary"
            @handleClick="handleLoadMore"
          />
        </div>
        <div class="col-span-full">
          <div v-for="(section, index) in sections" :key="index">
            <component
              v-if="section"
              :is="getComponent(section.name as ComponentType)"
              :ref="section.name"
              v-bind="{ ...section.props }"
            ></component>
          </div>
        </div>
        <!-- content end -->
      </page-wrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, computed, watch, onMounted } from "vue";
import { parseUrl, changeTheme, getThemeType } from "@/util/helper";

import PageWrapper from "@components/PageWrapper.vue";
import RichText from "@components/Typography/RichText.ce.vue";
import CustomButton from "@atoms/Button/CustomButton.ce.vue";
import FilterTag from "@components/Atoms/FilterTag/FilterTag.ce.vue";
import ProjectsGrid from "@organisms/ProjectsGrid/ProjectsGrid.vue";
import { getComponent, getComponentData } from "@/util/getComponent";
import { FilterType, Theme, IFilterTag } from "@/types/types";
import {
  getAllProjects,
  getFilterTags,
  getPage,
  getPageByID,
  getProjects,
  getTranslations,
} from "@/util/api";
import { ComponentType, FilterTagType } from "@/util/constants";
import { IProjectsPage } from "@/types/IProjectsPage";
import { IProjectArticlePage } from "@/types/IProjectArticlePage";

const isLoading = ref(true);
const isUmbraco = ref(false);

const environment = ref();
const urlParams = ref<String[] | null>(null);

const pageData = ref<IProjectsPage | null>(null);
const pageGUI = ref<string | undefined>();
const pageID = ref<string | undefined>();
const projectCards = ref<any[]>([]);

const tags = ref<IFilterTag[] | null>(null);
const activeFilter = ref("");
const pageNumber = ref(1);
const pageSize = ref(10);

const theme = ref<Theme | null>(null);
const bodyRef = ref<HTMLDivElement | null>(null);

const isPreview = ref<boolean>(false);
const previewUrl = ref<string>("");
const pageKey = ref<string | undefined>();

onBeforeMount(async () => {
  pageKey.value = document.documentElement.dataset.id;
  //get the meta property og:Url from the head tag
  const metaUrl = document.head.querySelector("meta[property='og:url']");
  if (metaUrl instanceof HTMLMetaElement) {
    previewUrl.value = metaUrl.content.replace(/^https?:\/\/[^/]+/, "");
  }

  environment.value = parseUrl(window.location.href).environment;
  urlParams.value = parseUrl(window.location.href).params;
  if (!urlParams.value) return;
  let path = urlParams.value.join("/");
  if (previewUrl.value.indexOf(path) < 0) {
    path = previewUrl.value;
    isPreview.value = true;
  }
  if (path) {
    pageData.value = (await getPageByID(pageKey.value, isPreview.value)) as IProjectsPage;
    //pageData.value = (await getPage(path, isPreview.value)) as IProjectsPage;
    const seeAllTagName = await getTranslations("SeeAllInsights");
    const seeAllTag = {
      id: "all",
      title: seeAllTagName,
      contentType: "sectorTag" as FilterType,
    } as IFilterTag;

    const tagFilters = await getFilterTags(FilterTagType.SECTOR_TAG);
    tagFilters.unshift(seeAllTag);
    tags.value = tagFilters;
    activeFilter.value = tags.value[0].id;

    if (pageData.value) {
      theme.value = pageData.value.properties.backgroundColor;
      if (theme.value) changeTheme(theme.value);
      pageGUI.value = pageData.value.route.startItem.id;
      pageID.value = document.documentElement.dataset.id;
      if (activeFilter.value) {
        const filteredInsight = await fetchProjects();
        if (!filteredInsight) return;
        projectCards.value = createProjectCards(filteredInsight);
      }

      isLoading.value = false;
    }
  }
});

function isTagHidden(tag: IFilterTag) {
  return tag.sectorToggle?.includes("hideFromProjects") ? false : true;
}

onMounted(() => {
  if (bodyRef.value) {
    bodyRef.value.style.transition = `background-color 0.3s linear`;
  }
});

watch(activeFilter, (value) => {
  if (value) {
    pageNumber.value = 1;
    fetchProjects().then((projects) => {
      if (!projects) return;
      projectCards.value = createProjectCards(projects);
    });
  }
});

const properties = computed(() => {
  return pageData.value?.properties;
});

const themeType = computed(() => {
  if (!theme.value) return "dark";
  return getThemeType(theme.value);
});

async function fetchProjects() {
  if (!pageID?.value) return;
  let projects: IProjectArticlePage[] = [];
  if (activeFilter.value === "all") {
    projects = await getAllProjects(pageNumber.value, pageSize.value);
  } else {
    projects = await getProjects(
      pageID.value,
      activeFilter.value,
      pageNumber.value,
      pageSize.value
    );
  }
  return projects;
}

function createProjectCards(projects: IProjectArticlePage[]): any[] {
  if (!projects) return [];
  const projectCards = projects.map((p: IProjectArticlePage) => {
    return {
      title: p.properties.title,
      tag: p.properties.tag,
      location: p.properties.location,
      client: p.properties.partners,
      image: JSON.stringify(p.properties.image[0]),
      imgAlt: p.properties.imageAltText,
      href: p.route.path,
      theme: theme?.value,
    };
  });
  return projectCards;
}

const headerStyle = computed(() => {
  const color =
    isUmbraco.value && themeType.value === "dark"
      ? "text-white"
      : isUmbraco.value && themeType.value === "light"
      ? "text-aubergine"
      : "text-typography";
  return `h1 mb-10 ${color} flex-1 lg:mb-0 lg:mr-10 themeTransition`;
});

async function handleLoadMore() {
  pageNumber.value++;
  const addedInsights = await fetchProjects();
  if (!addedInsights) return;
  const addedInsightsCards = createProjectCards(addedInsights);
  projectCards.value = [...projectCards.value, ...addedInsightsCards];
}

const showLoadMore = computed(() => {
  return projectCards.value.length === pageSize.value;
});

const sections = computed(() => {
  if (!properties.value || !properties?.value?.grid) return [];
  console.log("properties.value.grid.items: ", properties?.value?.grid);
  const sections = properties.value.grid.items.map((item) => {
    const { content } = item;
    if (!content) return;
    return getComponentData(content);
  });
  const sanitizedSections = sections.filter(Boolean);

  return sanitizedSections;
});
</script>
